import { reactive } from "vue";
import {
  schedulingApi,
  type Agenda,
  type Appointment,
  type AppointmentStatus,
  type LocationSettings,
  type TypeChoice,
} from "../api/scheduling";
import { adminApi } from "../api/admin";
import { flowsApi } from "../api/flows";
import { ApiError } from "../api/client";
import { errorText } from "../api/errors";
import { locale, t, type MessageKey } from "../i18n";
import { onRealtime } from "./inbox";
import { addDays, dayOf } from "./agendaTime";

/**
 * What the agenda screens share (decision 0022): the words for statuses and calendars, dates in
 * the agenda's zone and the browser's language, the names behind ids, and the realtime refresh.
 */
export const intlLocale = () =>
  locale.value === "en" ? "en-US" : locale.value;

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const noon = (day: string) => new Date(`${day}T12:00:00Z`);

/** "09:30" in the agenda's zone. */
export function timeText(at: string | Date, timeZone: string): string {
  return new Intl.DateTimeFormat(intlLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(new Date(at));
}

/** "Seg", "Ter"… */
export function shortWeekday(day: string): string {
  return capital(
    new Intl.DateTimeFormat(intlLocale(), {
      weekday: "short",
      timeZone: "UTC",
    })
      .format(noon(day))
      .replace(/\.$/, ""),
  );
}

/** "Segunda", "Sábado"… (without "-feira", as people say it). */
export function longWeekday(day: string): string {
  return capital(
    new Intl.DateTimeFormat(intlLocale(), {
      weekday: "long",
      timeZone: "UTC",
    })
      .format(noon(day))
      .replace(/-feira$/, ""),
  );
}

/** "27/10" (10/27 in English). */
export function dayMonth(day: string): string {
  return new Intl.DateTimeFormat(intlLocale(), {
    day: "2-digit",
    month: "2-digit",
    timeZone: "UTC",
  }).format(noon(day));
}

/** "Hoje", "Amanhã" or the weekday. */
export function relativeDay(day: string, today: string): string {
  if (day === today) return t("agendaview.today");
  if (day === addDays(today, 1)) return t("agendaview.tomorrow");
  return longWeekday(day);
}

/** "22/10 14:03": when something happened, in the agenda's zone. */
export function stampText(at: string, timeZone: string): string {
  return `${dayMonth(dayOf(at, timeZone))} ${timeText(at, timeZone)}`;
}

/** "Ter 28/10, 09:00–10:00". */
export function whenText(
  a: Pick<Appointment, "startsAt" | "endsAt" | "timezone">,
): string {
  const day = dayOf(a.startsAt, a.timezone);
  return t("appointment.when_value", {
    day: shortWeekday(day),
    date: dayMonth(day),
    start: timeText(a.startsAt, a.timezone),
    end: timeText(a.endsAt, a.timezone),
  });
}

export const STATUSES: AppointmentStatus[] = [
  "booked",
  "confirmed",
  "attended",
  "no_show",
  "cancelled",
];
export const statusLabel = (s: AppointmentStatus) =>
  t(`appointment.status.${s}` as MessageKey);
/** The StatusChip colour of each status. */
export const statusTone = (s: AppointmentStatus) =>
  ({
    booked: "waiting_contact",
    confirmed: "success",
    attended: "success",
    no_show: "failed",
    cancelled: "closed",
  })[s];

export const providerLabel = (p: string) =>
  ["google", "microsoft", "caldav", "ics"].includes(p)
    ? t(`agendaview.provider.${p}` as MessageKey)
    : p;

/** Where it happens, as the summary and the panel say it. */
export function placeText(
  l: LocationSettings,
  contactFields: Record<string, unknown> = {},
): string {
  if (l.kind === "address") return l.address;
  if (l.kind === "contact_field") {
    const v = contactFields[l.field];
    return typeof v === "string" && v.trim()
      ? v.trim()
      : t("appointment.place.contact_field");
  }
  if (l.kind === "online")
    return t(`appointment.place.online_${l.provider}` as MessageKey);
  if (l.kind === "phone") return t("appointment.place.phone");
  if (l.kind === "to_agree") return t("appointment.place.to_agree");
  return "";
}

/** The names behind ids (people, assistants, flows) and the company's agendas, once per company. */
interface Refs {
  agendas: Agenda[];
  types: TypeChoice[];
  people: Record<string, string>;
  assistants: Record<string, string>;
  flows: Record<string, string>;
  loaded: boolean;
}
const refs = reactive<Record<string, Refs>>({});
const loading = new Map<string, Promise<void>>();

export function agendaRefs(slug: string): Refs {
  if (!refs[slug]) {
    refs[slug] = {
      agendas: [],
      types: [],
      people: {},
      assistants: {},
      flows: {},
      loaded: false,
    };
    void reloadAgendaRefs(slug);
  }
  return refs[slug]!;
}

/** Fixed names without the API (the styleguide's sample appointment). */
export function seedAgendaRefs(slug: string, data: Partial<Refs>): void {
  refs[slug] = {
    agendas: [],
    types: [],
    people: {},
    assistants: {},
    flows: {},
    ...data,
    loaded: true,
  };
}

export function reloadAgendaRefs(slug: string): Promise<void> {
  const running = loading.get(slug);
  if (running) return running;
  const target = agendaRefs(slug);
  const p = Promise.all([
    schedulingApi.agendas(slug).catch(() => ({ agendas: [] as Agenda[] })),
    schedulingApi.types(slug).catch(() => ({ types: [] as TypeChoice[] })),
    adminApi.members(slug).catch(() => ({ members: [] })),
    adminApi.assistants(slug).catch(() => ({ assistants: [] })),
    flowsApi.list(slug).catch(() => ({ flows: [] })),
  ])
    .then(([a, ty, m, as, f]) => {
      target.agendas = a.agendas;
      target.types = ty.types;
      const people: Record<string, string> = {};
      for (const ag of a.agendas)
        for (const p of ag.members) people[p.memberId] = p.name;
      for (const p of m.members) people[p.id] = p.name;
      target.people = people;
      target.assistants = Object.fromEntries(
        as.assistants.map((x) => [x.id, x.name]),
      );
      target.flows = Object.fromEntries(f.flows.map((x) => [x.id, x.name]));
      target.loaded = true;
    })
    .finally(() => loading.delete(slug));
  loading.set(slug, p);
  return p;
}

export interface AppointmentEvent {
  appointmentId: string;
  change: string;
  memberIds: string[];
}
/** The workspace's realtime "appointment.updated" (the inbox socket carries it). */
export function onAppointmentChange(
  listener: (event: AppointmentEvent) => void,
): () => void {
  return onRealtime((e) => {
    if (e.type === "appointment.updated") listener(e);
  });
}

const KNOWN_ERRORS = [
  "slot_taken",
  "contact_has_appointment",
  "agenda_inactive",
  "agenda_has_no_people",
  "appointment_closed",
  "appointment_not_started",
  "appointment_not_found",
  "contact_not_found",
];
/** A refused booking or change in plain words; the codes this screen does not know go generic. */
export function schedulingError(err: unknown): string {
  if (err instanceof ApiError) {
    if (KNOWN_ERRORS.includes(err.code))
      return t(`appointment.error.${err.code}` as MessageKey);
    const details = err.details as Record<string, string[]> | undefined;
    if (
      err.code === "validation_failed" &&
      details?.memberId?.includes("member_not_in_agenda")
    )
      return t("appointment.error.member_not_in_agenda");
  }
  return errorText(err);
}
